'use client';

import Link from 'next/link';
import { FC, useEffect, useState } from 'react';

import { AuthButtons } from '../AuthButtons';
import { HeaderSkeleton } from '../HeaderSkeleton';
import { Logo } from '../Logo';
import { UserPanel } from '../UserBlock';
import { AnalyticsEventName } from '@/api/analytics';
import { Menu, MenuProps } from '@/components/general/Menu';
import { LabelsSection } from '@/components/LabelsSection';
import { Support } from '@/components/Support';
import { SwitchLanguage } from '@/components/SwitchLanguage';
import { generateLink } from '@/helpers/common';
import { useAnalytics } from '@/hooks/analytics';
import { useFixedHeader } from '@/hooks/header';
import { usePopupHeight } from '@/hooks/usePopupHeight';
import { useCommonStore } from '@/store';

import {
  ScAddons,
  ScHeaderButtonsGroup,
  ScHeaderColLeft,
  ScHeaderColRight,
  ScHeaderDesktop,
  ScHeaderLogo,
  ScHeaderRow,
} from '../styled';

interface HeaderDesktopProps extends MenuProps {
  locale: string;
}

const WIDE_HEADER_TOP_OFFSET = 84;

export const HeaderDesktop: FC<HeaderDesktopProps> = ({ locale, links }) => {
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingUserPure = useCommonStore.use.pendingUserPure();
  const isFixedHeader = useFixedHeader(locale);

  const [wideHeader, setIsWideHeader] = useState(false);

  const { sendEventToAnalytics } = useAnalytics();

  const { popupHeight } = usePopupHeight();

  const handleLUHomeClick = () => {
    sendEventToAnalytics({ name: AnalyticsEventName.LU_HOME_TOP });
  };

  useEffect(() => {
    setIsWideHeader(!isFixedHeader);
  }, [isFixedHeader]);

  useEffect(() => {
    if (isFixedHeader) {
      const handleScroll = () => {
        if (window?.scrollY > WIDE_HEADER_TOP_OFFSET && !wideHeader) {
          setIsWideHeader(true);
        } else if (window?.scrollY < WIDE_HEADER_TOP_OFFSET && wideHeader) {
          setIsWideHeader(false);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isFixedHeader, wideHeader, setIsWideHeader]);

  return (
    <ScHeaderDesktop
      isFixedHeader={isFixedHeader}
      wide={wideHeader}
      popupHeight={popupHeight}
    >
      <ScHeaderRow>
        <ScHeaderColLeft>
          <ScHeaderLogo>
            <Link
              id="logo"
              href={generateLink('/', locale)}
              onClick={handleLUHomeClick}
            >
              <Logo />
            </Link>
          </ScHeaderLogo>
          <Menu withStripe links={links} locale={locale} />
        </ScHeaderColLeft>
        {pendingUserPure ? (
          <ScHeaderColRight>
            <HeaderSkeleton />
          </ScHeaderColRight>
        ) : (
          <ScHeaderColRight>
            <ScHeaderButtonsGroup>
              <LabelsSection />
              {isAuthenticated ? <UserPanel /> : <AuthButtons />}
              {!isAuthenticated && <SwitchLanguage variant="compact" />}
              <ScAddons>
                <Support
                  eventName={
                    AnalyticsEventName.HEADER__BUTTONS__SUPPORT_BUTTON__CLICK
                  }
                />
              </ScAddons>
            </ScHeaderButtonsGroup>
          </ScHeaderColRight>
        )}
      </ScHeaderRow>
    </ScHeaderDesktop>
  );
};
